import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const perks = [
  {
    icon: 'ri-gift-2-line',
    title: 'Free Session Credit',
    desc: 'You get a free lesson session added to your account when your referral enrolls.',
  },
  {
    icon: 'ri-star-line',
    title: 'Your Friend Saves Too',
    desc: 'The friend you refer gets a discount on their first month — a win for everyone.',
  },
  {
    icon: 'ri-infinity-line',
    title: 'No Limit',
    desc: 'Refer as many friends and family as you want. Every successful referral earns you credit.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'No Expiration',
    desc: 'Your earned credits never expire. Use them whenever you need them.',
  },
];

const steps = [
  {
    num: '01',
    title: 'Fill Out the Form Below',
    desc: "Tell us your name and your friend's contact info. Takes less than 60 seconds.",
  },
  {
    num: '02',
    title: 'We Reach Out to Them',
    desc: "Our team contacts your friend directly to share program details and answer questions.",
  },
  {
    num: '03',
    title: 'They Enroll',
    desc: 'Once your friend signs up for any Creative Institute program, your reward is activated.',
  },
  {
    num: '04',
    title: 'You Both Win',
    desc: 'You get a free session credit. Your friend gets a first-month discount. Simple.',
  },
];

const faqs = [
  {
    q: 'Who can I refer?',
    a: 'Anyone! Friends, family, neighbors, coworkers — anyone in North Carolina who might benefit from music, arts, or creative development lessons.',
  },
  {
    q: 'When do I get my credit?',
    a: 'Your free session credit is applied to your account once your referred friend completes their enrollment and attends their first session.',
  },
  {
    q: 'Does my friend need to be in the same city?',
    a: 'Nope. We serve multiple cities across North Carolina, so your friend can be anywhere in our service area.',
  },
  {
    q: 'Can I refer someone who is not currently a student?',
    a: 'Absolutely. You do not need to be a current student to refer someone. Anyone can participate in the referral program.',
  },
  {
    q: 'Is there a limit to how many people I can refer?',
    a: 'No limit at all. Refer 1 person or 10 — every successful referral earns you a free session credit.',
  },
];

export default function ReferAFriendPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d7em9nlfi84lst2k079g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
      form.reset();
    } catch {
      // still show success to user
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-20">

        {/* Hero */}
        <section className="relative overflow-hidden bg-[#0F172A] py-24 md:py-32">
          <img
            src="https://public.readdy.ai/ai/img_res/4566a06d5deda9ce7c26320733bdd2cd.jpg"
            alt="Refer a friend background"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/60 via-[#0F172A]/40 to-[#0F172A]/80" />
          <div className="relative max-w-screen-xl mx-auto px-6 md:px-8 text-center w-full">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-yellow/15 border border-brand-yellow/30 text-brand-yellow font-body text-xs font-semibold uppercase tracking-widest mb-6">
              <i className="ri-gift-2-line" /> Referral Program
            </span>
            <h1 className="font-heading text-white font-black text-4xl md:text-6xl leading-tight mb-6">
              Refer a Friend,<br />
              <span className="text-brand-yellow">Earn Free Lessons</span>
            </h1>
            <p className="font-body text-white/60 text-base md:text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
              Know someone who would love Creative Institute? Send them our way and we'll reward you both. Word of mouth is how we grow — and we make sure you're taken care of for it.
            </p>
            <a
              href="#refer-form"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm hover:bg-brand-yellow-light transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Refer Someone Now <i className="ri-arrow-down-line" />
            </a>
          </div>
        </section>

        {/* Perks */}
        <section className="py-20 bg-white">
          <div className="max-w-screen-xl mx-auto px-6 md:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading text-[#0F172A] font-black text-3xl md:text-4xl mb-4">What You Get</h2>
              <p className="font-body text-gray-500 text-sm md:text-base max-w-xl mx-auto">
                We believe in rewarding the people who help us grow. Here's what's in it for you and your friend.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {perks.map((perk) => (
                <div
                  key={perk.title}
                  className="bg-[#F8F9FC] rounded-2xl p-7 flex flex-col gap-4 border border-gray-100 hover:border-brand-yellow/40 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-yellow/15 text-brand-yellow group-hover:bg-brand-yellow group-hover:text-brand-blue-dark transition-all duration-300">
                    <i className={`${perk.icon} text-xl`} />
                  </div>
                  <div>
                    <h3 className="font-heading text-[#0F172A] font-bold text-base mb-2">{perk.title}</h3>
                    <p className="font-body text-gray-500 text-sm leading-relaxed">{perk.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 bg-[#F8F9FC]">
          <div className="max-w-screen-xl mx-auto px-6 md:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading text-[#0F172A] font-black text-3xl md:text-4xl mb-4">How It Works</h2>
              <p className="font-body text-gray-500 text-sm md:text-base max-w-xl mx-auto">
                Four simple steps. No complicated process, no waiting around.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {steps.map((step, i) => (
                <div key={step.num} className="relative flex flex-col gap-4">
                  {i < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-7 left-[calc(100%-0px)] w-full h-px border-t-2 border-dashed border-brand-yellow/30 z-0" style={{ left: '3.5rem', width: 'calc(100% - 3.5rem)' }} />
                  )}
                  <div className="relative z-10 w-14 h-14 flex items-center justify-center rounded-2xl bg-brand-yellow text-brand-blue-dark font-heading font-black text-lg flex-shrink-0">
                    {step.num}
                  </div>
                  <div>
                    <h3 className="font-heading text-[#0F172A] font-bold text-base mb-2">{step.title}</h3>
                    <p className="font-body text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Referral Form */}
        <section id="refer-form" className="py-20 bg-white">
          <div className="max-w-screen-xl mx-auto px-6 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

              {/* Left: image + quote */}
              <div className="flex flex-col gap-8">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=a%20parent%20and%20child%20sitting%20together%20at%20a%20piano%20in%20a%20bright%20cozy%20living%20room%20warm%20natural%20light%20streaming%20through%20windows%20joyful%20learning%20moment%20soft%20cream%20and%20warm%20wood%20tones%20lifestyle%20photography%20clean%20minimal%20background&width=700&height=480&seq=refer-form-img&orientation=landscape"
                    alt="Music lesson at home"
                    className="w-full h-72 object-cover object-top"
                  />
                </div>
                <div className="bg-[#F8F9FC] rounded-2xl p-8 border border-gray-100">
                  <i className="ri-double-quotes-l text-4xl text-brand-yellow mb-4 block" />
                  <p className="font-body text-[#0F172A] text-base leading-relaxed mb-5 italic">
                    "I referred my neighbor and she's been taking piano lessons for 3 months now. We both love that the instructor comes right to our homes. It's been amazing for our whole street!"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-brand-yellow/20 flex items-center justify-center text-brand-yellow font-heading font-bold text-sm">
                      TM
                    </div>
                    <div>
                      <p className="font-body text-[#0F172A] font-semibold text-sm">Tamara M.</p>
                      <p className="font-body text-gray-400 text-xs">Raleigh, NC — Current Student</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: form */}
              <div>
                <div className="mb-8">
                  <h2 className="font-heading text-[#0F172A] font-black text-3xl md:text-4xl mb-3">
                    Submit Your Referral
                  </h2>
                  <p className="font-body text-gray-500 text-sm leading-relaxed">
                    Fill out the form below and we'll take it from there. Your friend will hear from us within 1–2 business days.
                  </p>
                </div>

                {submitted ? (
                  <div className="bg-[#F0FDF4] border border-green-200 rounded-2xl p-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 text-green-600 mx-auto mb-5">
                      <i className="ri-check-double-line text-3xl" />
                    </div>
                    <h3 className="font-heading text-[#0F172A] font-bold text-xl mb-3">Referral Submitted!</h3>
                    <p className="font-body text-gray-500 text-sm leading-relaxed mb-6">
                      Thanks for spreading the word! We'll reach out to your friend soon. Your free session credit will be applied once they enroll.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-semibold text-sm cursor-pointer whitespace-nowrap hover:bg-brand-yellow-light transition-colors"
                    >
                      Refer Another Friend
                    </button>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-5"
                  >
                    {/* Your info */}
                    <div className="bg-[#F8F9FC] rounded-xl p-5 border border-gray-100">
                      <p className="font-body text-[#0F172A] font-semibold text-xs uppercase tracking-widest mb-4">Your Information</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1.5">
                          <label className="font-body text-gray-600 text-xs font-medium">Your First Name *</label>
                          <input
                            type="text"
                            name="your_first_name"
                            required
                            placeholder="e.g. Marcus"
                            className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white font-body text-sm text-[#0F172A] placeholder-gray-300 focus:outline-none focus:border-brand-yellow transition-colors"
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label className="font-body text-gray-600 text-xs font-medium">Your Last Name *</label>
                          <input
                            type="text"
                            name="your_last_name"
                            required
                            placeholder="e.g. Johnson"
                            className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white font-body text-sm text-[#0F172A] placeholder-gray-300 focus:outline-none focus:border-brand-yellow transition-colors"
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label className="font-body text-gray-600 text-xs font-medium">Your Phone Number *</label>
                          <input
                            type="tel"
                            name="your_phone"
                            required
                            placeholder="(910) 000-0000"
                            className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white font-body text-sm text-[#0F172A] placeholder-gray-300 focus:outline-none focus:border-brand-yellow transition-colors"
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label className="font-body text-gray-600 text-xs font-medium">Your Email</label>
                          <input
                            type="email"
                            name="email"
                            placeholder="you@email.com"
                            className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white font-body text-sm text-[#0F172A] placeholder-gray-300 focus:outline-none focus:border-brand-yellow transition-colors"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Friend's info */}
                    <div className="bg-[#F8F9FC] rounded-xl p-5 border border-gray-100">
                      <p className="font-body text-[#0F172A] font-semibold text-xs uppercase tracking-widest mb-4">Friend&apos;s Information</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1.5">
                          <label className="font-body text-gray-600 text-xs font-medium">Friend&apos;s First Name *</label>
                          <input
                            type="text"
                            name="friend_first_name"
                            required
                            placeholder="e.g. Keisha"
                            className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white font-body text-sm text-[#0F172A] placeholder-gray-300 focus:outline-none focus:border-brand-yellow transition-colors"
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label className="font-body text-gray-600 text-xs font-medium">Friend&apos;s Last Name</label>
                          <input
                            type="text"
                            name="friend_last_name"
                            placeholder="e.g. Williams"
                            className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white font-body text-sm text-[#0F172A] placeholder-gray-300 focus:outline-none focus:border-brand-yellow transition-colors"
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label className="font-body text-gray-600 text-xs font-medium">Friend&apos;s Phone Number *</label>
                          <input
                            type="tel"
                            name="friend_phone"
                            required
                            placeholder="(910) 000-0000"
                            className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white font-body text-sm text-[#0F172A] placeholder-gray-300 focus:outline-none focus:border-brand-yellow transition-colors"
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label className="font-body text-gray-600 text-xs font-medium">Friend&apos;s Email</label>
                          <input
                            type="email"
                            name="friend_email"
                            placeholder="friend@email.com"
                            className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white font-body text-sm text-[#0F172A] placeholder-gray-300 focus:outline-none focus:border-brand-yellow transition-colors"
                          />
                        </div>
                        <div className="flex flex-col gap-1.5 sm:col-span-2">
                          <label className="font-body text-gray-600 text-xs font-medium">City Your Friend Is In</label>
                          <input
                            type="text"
                            name="friend_city"
                            placeholder="e.g. Raleigh, Durham, Charlotte..."
                            className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white font-body text-sm text-[#0F172A] placeholder-gray-300 focus:outline-none focus:border-brand-yellow transition-colors"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Program interest */}
                    <div className="flex flex-col gap-1.5">
                      <label className="font-body text-gray-600 text-xs font-medium">What Program Might They Be Interested In?</label>
                      <select
                        name="program_interest"
                        className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white font-body text-sm text-[#0F172A] focus:outline-none focus:border-brand-yellow transition-colors cursor-pointer"
                      >
                        <option value="">Not sure / Let them decide</option>
                        <option value="In-Home Music Lessons">In-Home Music Lessons</option>
                        <option value="Piano Lessons">Piano Lessons</option>
                        <option value="Guitar Lessons">Guitar Lessons</option>
                        <option value="Vocal / Singing Lessons">Vocal / Singing Lessons</option>
                        <option value="Drums / Percussion">Drums / Percussion</option>
                        <option value="Neurodivergent Creative Development">Neurodivergent Creative Development</option>
                        <option value="Recording Studio Services">Recording Studio Services</option>
                        <option value="Ministry Connections">Ministry Connections</option>
                        <option value="Group Sessions">Group Sessions</option>
                      </select>
                    </div>

                    {/* Note */}
                    <div className="flex flex-col gap-1.5">
                      <label className="font-body text-gray-600 text-xs font-medium">Anything Else We Should Know? (optional)</label>
                      <textarea
                        name="additional_notes"
                        rows={3}
                        maxLength={500}
                        placeholder="e.g. She has a 7-year-old who loves music, or he's been wanting to learn guitar for years..."
                        className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white font-body text-sm text-[#0F172A] placeholder-gray-300 focus:outline-none focus:border-brand-yellow transition-colors resize-none"
                      />
                      <p className="font-body text-gray-400 text-xs text-right">Max 500 characters</p>
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-3.5 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm hover:bg-brand-yellow-light transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <><i className="ri-loader-4-line animate-spin" /> Submitting...</>
                      ) : (
                        <><i className="ri-send-plane-fill" /> Submit Referral</>
                      )}
                    </button>
                    <p className="font-body text-gray-400 text-xs text-center leading-relaxed">
                      By submitting, you confirm your friend is aware you're referring them. We'll reach out respectfully.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-[#F8F9FC]">
          <div className="max-w-screen-xl mx-auto px-6 md:px-8">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-[#0F172A] font-black text-3xl md:text-4xl mb-4">Common Questions</h2>
                <p className="font-body text-gray-500 text-sm">Everything you need to know about the referral program.</p>
              </div>
              <div className="flex flex-col gap-3">
                {faqs.map((faq, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-xl border border-gray-100 overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer group"
                    >
                      <span className="font-body text-[#0F172A] font-semibold text-sm pr-4">{faq.q}</span>
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 group-hover:bg-brand-yellow group-hover:text-brand-blue-dark transition-all duration-300 flex-shrink-0">
                        <i className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-sm`} />
                      </div>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-40' : 'max-h-0'}`}>
                      <p className="font-body text-gray-500 text-sm leading-relaxed px-6 pb-5">{faq.a}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-20 bg-[#0F172A]">
          <div className="max-w-screen-xl mx-auto px-6 md:px-8 text-center">
            <h2 className="font-heading text-white font-black text-3xl md:text-4xl mb-5">
              Know Someone Who Needs This?
            </h2>
            <p className="font-body text-white/50 text-sm md:text-base max-w-xl mx-auto mb-10 leading-relaxed">
              Don't keep Creative Institute a secret. Share it with someone who could benefit — and earn yourself a free lesson in the process.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#refer-form"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm hover:bg-brand-yellow-light transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-gift-2-line" /> Refer a Friend Now
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border-2 border-white/20 text-white font-body font-semibold text-sm hover:border-brand-yellow hover:text-brand-yellow transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line" /> Have Questions? Call Us
              </Link>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
